import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, EMPTY } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';
import { Sample } from 'src/app/models/sample.model';
import { Lookup, GroupLookup } from 'src/app/models/lookup.model';
import { AgentGroup } from 'src/app/models/agentGroup.model';
import { SampleCategoryEnum } from 'src/app/enums/sampleCategory.enum';
import { OperatorTypeEnum } from 'src/app/enums/operatorType.enum';
import { AgentService } from 'src/app/services/agent.service';
import { AgentGroupService } from 'src/app/services/agentGroup.service';
import { AgentJurisdictionService } from 'src/app/services/agent-jurisdiction.service';
import { SegOrgLevelService } from 'src/app/services/seg-org-level.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { SegOrgLevel } from 'src/app/models/segOrgLevel.model';
import { FormField } from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { AgentJurisdiction } from 'src/app/models/agent-jurisdiction.model';
import { SegShiftValues } from './sample-agent-jurisdiction.model';

@Injectable()
export class SampleAgentJurisdictionService {

  constructor(
    private agentService: AgentService,
    private agentGroupService: AgentGroupService,
    private agentJurisdictionService: AgentJurisdictionService,
    private segOrgLevelService: SegOrgLevelService,
    private organizationLevelService: OrganizationLevelService
  ) { }

  /**
   * Update agents list based on sample type
   */
  updateAgentsList(
    entity: Sample,
    selectedOrgLevel: { id?: string } | null,
    agents: Lookup[],
    fields: FormField[]
  ): Observable<Lookup[]> {
    const filter = JSON.stringify({
      organizationLevelId: selectedOrgLevel?.id ?? entity.organizationLevelId,
      agentType: entity.sampleType
    });

    return this.agentService.getAll(filter).pipe(
      map((agentList: Lookup[]) => {
        const agentField = fields?.find(f => f.property === nameOf<Sample>(x => x.agentIds));
        if (agentField) {
          agentField.options = agentList;
        }
        return agentList;
      })
    );
  }

  /**
   * Get agents in a group
   */
  getGroupAgents(
    entity: Sample,
    allAgents: Lookup[],
    fields: FormField[]
  ): Observable<{
    groupAgents: Lookup[];
    remainingAgents: Lookup[];
  }> {
    if (!entity.agentGroupId) {
      entity.agentIds = [];
      const agentIdField = fields?.find(f => f.property === nameOf<Sample>(x => x.agentIds));
      if (agentIdField) agentIdField.options = allAgents;
      const selectedField = fields?.find(f => f.property === nameOf<Sample>(x => x.selectedGroupAgents));
      if (selectedField) selectedField.options = [];
      return of({ groupAgents: [], remainingAgents: allAgents });
    }

    return this.agentGroupService.getById(entity.agentGroupId).pipe(
      switchMap((res: AgentGroup) => {
        const agentIds = res?.agentIds ?? [];
        entity.agentIds = [];

        if (!agentIds.length) {
          const agentIdField = fields?.find(f => f.property === nameOf<Sample>(x => x.agentIds));
          if (agentIdField) agentIdField.options = allAgents;
          const selectedField = fields?.find(f => f.property === nameOf<Sample>(x => x.selectedGroupAgents));
          if (selectedField) selectedField.options = [];
          return of({ groupAgents: [], remainingAgents: allAgents });
        }

        const agentPayload = {
          OperatorTypeEnum: JSON.stringify({
            Condition: OperatorTypeEnum.Equal,
            Field: 'id',
            Value: (res?.agentIds || []).join(', ')
          })
        };

        return this.agentService.getAll(JSON.stringify(agentPayload)).pipe(
          map((agents: Lookup[]) => {
            const remainingAgents = allAgents.filter(agent => !agents.some(g => g.id === agent.id));
            
            const agentIdField = fields?.find(f => f.property === nameOf<Sample>(x => x.agentIds));
            if (agentIdField) agentIdField.options = remainingAgents;

            entity.selectedGroupAgents = agents.map(agent => String(agent.id));
            const selectedField = fields?.find(f => f.property === nameOf<Sample>(x => x.selectedGroupAgents));
            if (selectedField) selectedField.options = agents;

            return { groupAgents: agents, remainingAgents };
          })
        );
      })
    );
  }

  /**
   * Update jurisdictions based on selected agents
   */
  updateJurisdictions(
    entity: Sample,
    allJurisdictions: { id: string; [key: string]: unknown }[],
    fields: FormField[]
  ): Observable<Lookup[]> {
    const agentIds = entity.agentIds || [];
    const jurisdictionIds = entity.jurisdictionIds || [];

    if (!agentIds.length) {
      entity.jurisdictionIds = [];
      entity.oelTypeId = null;
      return of([]);
    }

    // Fetch details for each agent
    const apis = agentIds.map(agentId => this.agentJurisdictionService.getAgentDetails(agentId));

    return forkJoin(apis).pipe(
      map((responses: AgentJurisdiction[][]) => {
        // Flatten responses array since each api call returns AgentJurisdiction[]
        const flattenedResponses = responses.flat();
        
        // Extract unique jurisdictions from responses by jurisdictionId
        const jurisdictionMap = new Map<string, Lookup>();
        flattenedResponses
          .filter(item => item.jurisdictionId != null)
          .forEach(item => {
            if (!jurisdictionMap.has(item.jurisdictionId)) {
              const fromAll = allJurisdictions.find(x => x.id === item.jurisdictionId);
              // Create a Lookup with id and name (jurisdictionName from AgentJurisdiction)
              jurisdictionMap.set(item.jurisdictionId, {
                id: item.jurisdictionId,
                name: (fromAll?.name as string) || item.jurisdictionName || '',
                ...fromAll
              });
            }
          });
        const jurisdictions: Lookup[] = [...jurisdictionMap.values()];

        // Extract unique OEL types from responses by oelTypeId
        const oelTypeMap = new Map<string, Lookup>();
        flattenedResponses
          .filter(item => item.oelTypeId != null)
          .forEach(item => {
            if (!oelTypeMap.has(item.oelTypeId)) {
              oelTypeMap.set(item.oelTypeId, {
                id: item.oelTypeId,
                name: item.oelTypeName || ''
              });
            }
          });
        const oelTypes: Lookup[] = [...oelTypeMap.values()];

        const jurisdictionsField = fields?.find(f => f.property === nameOf<Sample>(x => x.jurisdictionIds));
        if (jurisdictionsField) {
          jurisdictionsField.options = jurisdictions;
        }

        const oelTypeField = fields?.find(f => f.property === nameOf<Sample>(x => x.oelTypeId));
        if (oelTypeField) {
          oelTypeField.options = oelTypes;
        }

        return jurisdictions;
      })
    );
  }

  /**
   * Update agent-related fields based on selection
   */
  updateAgentRadio(
    entity: Sample,
    useAgentGroups: boolean,
    isAdd: boolean,
    fields: FormField[]
  ): void {
    if (useAgentGroups) {
      entity.agentIds = [];
    } else {
      if (isAdd) {
        entity.agentGroupId = null;
      }
    }

    // Reset dependent fields
    if (!(entity.sampleCategory === SampleCategoryEnum.Blank)) {
      entity.oelTypeId = null;
    }
    entity.jurisdictionIds = [];
  }

  /**
   * Get the SEG's shift values (shift length, average weekly hours, shift roster) for the
   * entity's selected SEG + organization level, falling back to the parent site's SEG org level.
   */
  getSegOrgs(entity: Sample): Observable<SegShiftValues | null> {
    const { segId, organizationLevelId } = entity;

    if (!segId || !organizationLevelId) {
      return of(null);
    }

    const toShiftValues = (segOrg: SegOrgLevel): SegShiftValues => ({
      shiftLength: segOrg.shiftLengthHours,
      averageWeeklyHours_Seconds: segOrg.avgWeeklyHours,
      shiftRoster: segOrg.shiftType
    });

    return this.segOrgLevelService.getBySegId(segId, organizationLevelId).pipe(
      map(segOrg => toShiftValues(segOrg)),
      catchError(() =>
        this.organizationLevelService.getParentSite(organizationLevelId).pipe(
          switchMap(parentSite =>
            parentSite?.id
              ? this.segOrgLevelService.getBySegId(segId, parentSite.id).pipe(map(segOrg => toShiftValues(segOrg)))
              : of(null)
          ),
          catchError(() => of(null))
        )
      )
    );
  }

  /**
   * Get site info and default pressure
   */
  getSiteInfo(entity: Sample): Observable<void> {
    const { organizationLevelId, pressureMmhg } = entity;
    
    if (!organizationLevelId) {
      return of(void 0);
    }

    return this.organizationLevelService.getParentSite(organizationLevelId).pipe(
      switchMap(site => site?.id ? this.organizationLevelService.getById(site.id) : EMPTY),
      tap(org => { if (!pressureMmhg && org.defaultPressureMmHg) entity.pressureMmhg = org.defaultPressureMmHg; }),
      map(() => void 0),
      catchError(() => of(void 0))
    );
  }
}
